import { RefreshCw, Rocket, X } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

const TOAST_ID = 'app-version-update'

interface VersionUpdateToastProps {
  toastId: number | string
}

/**
 * Branded content for the "new version available" toast. Rendered via
 * `toast.custom` with `unstyled` so it bypasses the app's solid-blue default
 * Sonner theming and presents a light, on-brand card with a clear reload action.
 */
function VersionUpdateToast({ toastId }: VersionUpdateToastProps) {
  const { t } = useTranslation()

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="flex w-[356px] max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-lg shadow-primary/5"
    >
      <div className="flex items-start gap-3">
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 2.4, ease: 'easeInOut', repeat: Number.POSITIVE_INFINITY }}
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent ring-4 ring-primary/10"
        >
          <Rocket className="size-5 text-white" />
        </motion.div>

        <div className="flex min-w-0 flex-col gap-0.5 pt-0.5">
          <p className="text-sm font-semibold text-neutral-900">{t('versionUpdate.title')}</p>
          <p className="text-xs leading-relaxed text-neutral-500">
            {t('versionUpdate.description')}
          </p>
        </div>

        <button
          type="button"
          aria-label={t('versionUpdate.dismiss')}
          onClick={() => toast.dismiss(toastId)}
          className="-mr-1 -mt-1 flex size-6 shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="size-4" />
        </button>
      </div>

      <Button size="sm" onClick={() => window.location.reload()} className="w-full">
        <RefreshCw className="size-4" />
        {t('versionUpdate.reload')}
      </Button>
    </motion.div>
  )
}

/**
 * Shows the branded new-version toast (idempotent via a fixed id) and keeps it
 * pinned until the user reloads or dismisses it.
 */
export function showVersionUpdateToast(): void {
  toast.custom((id) => <VersionUpdateToast toastId={id} />, {
    id: TOAST_ID,
    duration: Number.POSITIVE_INFINITY,
    unstyled: true,
    className: 'version-update-toast',
  })
}
